import { useEffect, useState } from 'react';
import { isChildModeEnabled } from '@/utils/childMode';

type Options = {
  // Skickas in där SupervisorContext finns i scope (CreateActivityPage,
  // ChildOnboardingGate) så att källan blir live/reaktiv via React-state.
  // Utelämnas i App.tsx (ThemeProvider ligger UTANFÖR SupervisorScene) — då
  // faller hooken tillbaka på sessionStorage/localStorage, som speglar samma
  // sanning tack vare att SupervisorContext alltid skriver dit + dispatchar
  // 'storage' (se SupervisorContext.setClient/setSupervising).
  currentClient?: unknown;
  isSupervising?: boolean;
};

/**
 * ENDA sanningen för barnvyns gate (NPF). Tidigare härleddes
 * effectiveChildMode på tre ställen (App.tsx, CreateActivityPage,
 * ChildOnboardingGate) med olika källor och olika reaktivitet, vilket gav
 * inkonsistenta svar vid handledar-avval. Den här hooken centraliserar
 * formeln och reaktiviteten.
 *
 * Formel: effectiveChildMode = childMode && !currentClient && !isSupervising
 *   - childMode: kontots NPF-flagga (localStorage), läses reaktivt via
 *     'storage'-eventet (setChildMode dispatchar det i samma flik).
 *   - !currentClient: en handledare som valt en klient ska aldrig se barntemat.
 *   - !isSupervising: ett konto som agerar handledare (childMode på men ingen
 *     klient vald ännu) ska heller aldrig hamna i barnvyn/onboarding-redirect.
 *
 * Källval för currentClient/isSupervising:
 *   - Med Options (context i scope): live-värden från SupervisorContext.
 *   - Utan Options (App.tsx): sessionStorage 'currentClient' + localStorage
 *     'supervising'. Båda hålls i synk av SupervisorContext och triggar
 *     'storage', så fallbacket är lika reaktivt som context-vägen.
 */
export const useEffectiveChildMode = (options?: Options): boolean => {
  const [childMode, setChildMode] = useState<boolean>(isChildModeEnabled());
  const [fallbackSupervisedClient, setFallbackSupervisedClient] = useState<string | null>(() =>
    sessionStorage.getItem('currentClient'),
  );
  const [fallbackSupervising, setFallbackSupervising] = useState<string | null>(() =>
    localStorage.getItem('supervising'),
  );

  useEffect(() => {
    const handleStorageChange = () => {
      setChildMode(isChildModeEnabled());
      setFallbackSupervisedClient(sessionStorage.getItem('currentClient'));
      setFallbackSupervising(localStorage.getItem('supervising'));
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const hasClient =
    options !== undefined ? options.currentClient != null : fallbackSupervisedClient != null;

  const isSupervising =
    options?.isSupervising !== undefined
      ? options.isSupervising
      : !!fallbackSupervising && fallbackSupervising.length > 0;

  return childMode && !hasClient && !isSupervising;
};
